import { ReactNode } from 'react';

import { OrdersModel } from '@pukka/api';
import { numberUtils } from '@pukka/common/utils';
import { Text } from '@pukka/ui-components';
import sortBy from 'lodash/sortBy';

import { invoiceReceiptDateTime } from './utils';

interface RowProps {
  readonly label: ReactNode;
  readonly value: ReactNode;
}

interface RedemptionOptionListProps {
  readonly redemptionOptions: OrdersModel.RedemptionOption[];
}

interface ReceiptLoyaltyInfoProps {
  readonly loyalty?: OrdersModel.Loyalty | null;
  readonly memberPhone: string;
  readonly totalAmountDue: number;
}

function Row({ label, value }: RowProps) {
  return (
    <div className="flex justify-between">
      <Text variant="body-1">{label}</Text>
      <Text variant="body-1">{value}</Text>
    </div>
  );
}

function RedemptionOptionList({ redemptionOptions }: RedemptionOptionListProps) {
  return (
    <div className="mt-2" data-testid="redemption-options">
      {redemptionOptions.map((opt, idx) => {
        return (
          <div key={idx}>
            <Text variant="body-1">
              {opt.type === 'ELIGIBLE' ? 'Redeem' : 'Collect until'} {numberUtils.formatCurrency(opt.redeem_points)} points to earn{' '}
              {opt.reward}
            </Text>
            {opt.max_discount_amount && (
              <Text variant="caption-1" className="pl-2">
                *Maximum Discount Value {numberUtils.formatCurrency(opt.max_discount_amount, 'Rp.')}
              </Text>
            )}
            {opt.min_purchase_amount && (
              <Text variant="caption-1" className="pl-2">
                *Minimum Purchase {numberUtils.formatCurrency(opt.min_purchase_amount, 'Rp.')}
              </Text>
            )}
          </div>
        );
      })}
      <Text variant="body-1">Ask our staff about other rewards</Text>
    </div>
  );
}

const { formatDecimal } = numberUtils;

function ReceiptLoyaltyInfo({ loyalty, memberPhone, totalAmountDue }: ReceiptLoyaltyInfoProps) {
  if (!loyalty || Object.keys(loyalty).length === 0) {
    return null;
  }

  const isUnpaidInvoiceEarningPoints = totalAmountDue > 0 && loyalty.type === 'EARNING';
  const redemptionOptions = sortBy(loyalty.redemption_options, 'type');

  return (
    <div>
      <Text variant="title-1" color="blue" weight="semibold">
        Loyalty Program
      </Text>
      <div className="mt-2 rounded-lg bg-neutral-50 px-4 py-3">
        <div className="gap-2 sm:flex">
          <div className="flex-1">
            <Row label={<b>Member Since</b>} value={invoiceReceiptDateTime(loyalty.member_since, 'date')} />
            <Row label={<b>Member Number</b>} value={memberPhone} />
          </div>
          {!isUnpaidInvoiceEarningPoints && (
            <div className="flex-1 sm:border-l-2 sm:pl-4">
              <div>
                <Text variant="body-1" weight="semibold">
                  Loyalty Points
                </Text>
                <Row label="Current Balance" value={formatDecimal(loyalty.current_balance)} />
                {Boolean(loyalty.new_member_points) && <Row label="New Member Points" value={formatDecimal(loyalty.new_member_points)} />}
                <Row label="New Earned Points" value={formatDecimal(loyalty.new_earned_points)} />
                <Row label="Redeemed in This Purchase" value={formatDecimal(loyalty.redemption ? loyalty.redemption.redeem_points : 0)} />
                <Row label="Closing Balance" value={formatDecimal(loyalty.closing_balance)} />
              </div>
            </div>
          )}
        </div>

        {redemptionOptions.length > 0 && <RedemptionOptionList redemptionOptions={redemptionOptions} />}

        {isUnpaidInvoiceEarningPoints && (
          <Text variant="caption-1">
            Member can earn {formatDecimal(loyalty.new_earned_points + loyalty.new_member_points)} points after the invoice payment is fully
            paid.
          </Text>
        )}
      </div>
    </div>
  );
}

export { ReceiptLoyaltyInfo as default, RedemptionOptionList };
